import { BOM, BOMFormData } from '@/types/bom';
import { supabase } from '@/lib/supabase/client';
import { BOMBaseState } from './types';
import { logger } from './logger';

export const createBOMBaseSlice = (
  set: (state: Partial<BOMBaseState> | ((state: BOMBaseState) => Partial<BOMBaseState>)) => void,
  get: () => BOMBaseState
): BOMBaseState => ({
  boms: [],
  isLoading: false,
  error: null,
  initialized: false,

  fetchBOMs: async () => {
    // Skip if already initialized
    if (get().initialized && get().boms.length > 0) return;

    try {
      set({ isLoading: true, error: null });
      logger.info('Fetching BOMs');
      
      const { data, error } = await supabase
        .from('boms')
        .select(`
          *,
          project:project_id (id, name),
          design:design_id (id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error(`Error fetching BOMs: ${error.message}`);
        throw error;
      }
      
      logger.info(`Fetched ${data?.length || 0} BOMs`);
      set({ boms: data || [], initialized: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch BOMs';
      logger.error(`Failed to fetch BOMs: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  createBOM: async (data) => {
    try {
      set({ isLoading: true, error: null });
      logger.info(`Creating new BOM: ${data.name}`);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.error('No authenticated user found');
        throw new Error('No authenticated user');
      }

      // Create BOM
      logger.debug('Inserting BOM record');
      const { data: bom, error: bomError } = await supabase
        .from('boms')
        .insert([{
          name: data.name,
          project_id: data.project_id,
          design_id: data.design_id,
          type: data.type,
          status: 'draft',
          notes: data.notes,
          created_by: user.id
        }])
        .select(`
          *,
          project:project_id (
            id,
            name
          ),
          design:design_id (
            id,
            name
          )
        `)
        .single();

      if (bomError) {
        logger.error(`Error creating BOM: ${bomError.message}`);
        throw bomError;
      }

      // Handle components if provided in the new format
      if (data.components && data.components.length > 0) {
        logger.debug(`Inserting ${data.components.length} components`);
        // Insert components with parent-child relationships
        const { error: componentsError } = await supabase
          .from('bom_components')
          .insert(data.components.map(component => ({
            ...component,
            bom_id: bom.id
          })));

        if (componentsError) {
          logger.error(`Error inserting components: ${componentsError.message}`);
          throw componentsError;
        }
      } 
      // Legacy support for old BOM format
      else if (data.type === 'mechanical' && data.mechanical_items) {
        logger.debug(`Inserting ${data.mechanical_items.length} mechanical items (legacy format)`);
        const { error: itemsError } = await supabase
          .from('mechanical_bom_items')
          .insert(data.mechanical_items.map(item => ({
            ...item,
            bom_id: bom.id
          })));

        if (itemsError) {
          logger.error(`Error inserting mechanical items: ${itemsError.message}`);
          throw itemsError;
        }
      } else if (data.type === 'commercial' && data.commercial_items) {
        logger.debug(`Inserting ${data.commercial_items.length} commercial items (legacy format)`);
        const { error: itemsError } = await supabase
          .from('commercial_bom_items')
          .insert(data.commercial_items.map(item => ({
            ...item,
            bom_id: bom.id
          })));

        if (itemsError) {
          logger.error(`Error inserting commercial items: ${itemsError.message}`);
          throw itemsError;
        }
      }

      // Refresh BOMs list
      logger.info(`BOM created successfully with ID: ${bom.id}`);
      await get().fetchBOMs();

      return bom;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create BOM';
      logger.error(`BOM creation failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateBOM: async (id, data) => {
    try {
      set({ isLoading: true, error: null });
      logger.info(`Updating BOM: ${id}`);

      const { error: bomError } = await supabase
        .from('boms')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (bomError) {
        logger.error(`Error updating BOM: ${bomError.message}`);
        throw bomError;
      }

      logger.info(`BOM ${id} updated successfully`);
      // Refresh BOMs list
      await get().fetchBOMs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update BOM';
      logger.error(`BOM update failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteBOM: async (id) => {
    try {
      set({ isLoading: true, error: null });
      logger.info(`Deleting BOM: ${id}`);
      
      const { error } = await supabase
        .from('boms')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error(`Error deleting BOM: ${error.message}`);
        throw error;
      }

      logger.info(`BOM ${id} deleted successfully`);
      // Update local state
      set((state: BOMBaseState) => ({
        boms: state.boms.filter((bom: BOM) => bom.id !== id)
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete BOM';
      logger.error(`BOM deletion failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  approveBOM: async (id) => {
    try {
      set({ isLoading: true, error: null });
      logger.info(`Approving BOM: ${id}`);
      
      const { error } = await supabase
        .from('boms')
        .update({ 
          status: 'approved',
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        logger.error(`Error approving BOM: ${error.message}`);
        throw error;
      }

      logger.info(`BOM ${id} approved successfully`);
      await get().fetchBOMs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve BOM';
      logger.error(`BOM approval failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  rejectBOM: async (id, reason) => {
    try {
      set({ isLoading: true, error: null });
      logger.info(`Rejecting BOM: ${id}`);
      
      const { error } = await supabase
        .from('boms')
        .update({ 
          status: 'rejected',
          notes: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        logger.error(`Error rejecting BOM: ${error.message}`);
        throw error;
      }

      logger.info(`BOM ${id} rejected successfully`);
      await get().fetchBOMs();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject BOM';
      logger.error(`BOM rejection failed: ${message}`);
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
});
